/**
 * Typed-client helpers — the "consume the contract" half of `@kraahl/contract`.
 *
 * The schema files in this package describe what each endpoint promises to
 * return. Without a parser that actually RUNS those schemas at the boundary,
 * the contract is documentation only — a renamed field on the server still
 * silently `undefined`s on every client (the class of bug
 * `API_RESPONSE_AUDIT.md` was filed to close).
 *
 * `parseApiResponse` is the boundary parser. Callers wire it in their fetch
 * site: `fetch(url) → parseApiResponse(res, schema)`. A mismatch throws
 * `ApiResponseParseError` carrying the Zod issue list + raw body — callers
 * choose what to do with it (mobile fail-open: catch and return cached
 * fallback; web write-paths: bubble to a structured error toast).
 *
 * Transport (auth header, base URL, retries, timeouts) is intentionally
 * NOT in scope. Different clients have different needs — `kraahlFetch` on
 * mobile bolts on a Bearer header from the auth session; web routes use
 * Next's standard `fetch`. Embedding either here would re-couple the
 * contract layer to a delivery concern (`hex` skill, layer boundary).
 *
 * Pilot consumer: `apps/mobile/lib/version-gate.ts` (PR #254).
 *
 * @see RELIABILITY_ROADMAP.md §4 — typed-client + ESLint fence
 */

import { z } from "zod";

export class ApiResponseParseError extends Error {
  readonly issues: z.core.$ZodIssue[];
  readonly raw: unknown;

  constructor(issues: z.core.$ZodIssue[], raw: unknown) {
    const summary = issues
      .slice(0, 3)
      .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("; ");
    const more = issues.length > 3 ? ` (+${issues.length - 3} more)` : "";
    super(`API response failed schema validation: ${summary}${more}`);
    this.name = "ApiResponseParseError";
    this.issues = issues;
    this.raw = raw;
  }
}

/**
 * Shared error envelope returned by `/api/*` routes on non-2xx. Not part of
 * any frozen response contract — clients branch on HTTP status first, then
 * read `code` / `error` opportunistically. The shape exists so route
 * handlers can declare `NextResponse<XxxResponse | ApiErrorEnvelope>` and
 * have their error returns type-check against the function signature without
 * widening the success branch.
 *
 * Loose by design: `error` is human-facing string when present, omitted on
 * apiJson-style routes that don't carry it. `details` is opaque — concrete
 * shape depends on the failure mode (Zod issues, PayPal debug ids, etc.).
 *
 * Optional `paypalIssue` is populated on the money routes (`POST
 * /api/paypal-orders/create` + capture) when the underlying PayPal error
 * surfaced a structured `details[0].issue` code (PAYEE_ACCOUNT_RESTRICTED
 * et al). Lets the client render a "(KYC issue)" sub-line / "this isn't
 * your account" copy without re-parsing the raw PayPal body, and keeps
 * mobile + web on one shape. Optional everywhere else (other surfaces
 * never set it).
 *
 * Optional `debugId` mirrors the PayPal-Debug-Id passthrough that
 * `CreateOrderResponse.debugId` carries on the success path — surfaced
 * here on failures so support tooling can grep a single id across both
 * branches.
 *
 * @see .tondocs/paypal/HANDOFF_DROP_RESILIENCE_2026_06_26.md T1
 */
export interface ApiErrorEnvelope {
  error?: string;
  code: string;
  details?: unknown;
  paypalIssue?: string;
  // Permits `null` because PayPal routes pass `response.headers.get("paypal-debug-id")`
  // verbatim — the header may genuinely be absent on non-PayPal failures.
  // Optional everywhere else.
  debugId?: string | null;
}

/**
 * Read a `Response`'s JSON body and validate it against the contract schema.
 * Throws `ApiResponseParseError` on shape mismatch (including malformed JSON);
 * returns the parsed value typed as `z.infer<Schema>` on success.
 *
 * NOTE: this does NOT check `res.ok`. HTTP status semantics are the caller's
 * concern — a 503 with `{ code: "MIN_VERSION_UNAVAILABLE" }` is a perfectly
 * valid wire shape that may want its own schema, not a "thrown error".
 * Callers decide whether to skip parsing on non-2xx (mobile version-gate)
 * or parse both branches and switch (future hard-fail paths).
 */
export async function parseApiResponse<T>(
  res: Response,
  schema: z.ZodType<T>,
): Promise<T> {
  let raw: unknown;
  try {
    raw = await res.json();
  } catch (err) {
    throw new ApiResponseParseError(
      [
        {
          code: "custom",
          path: [],
          message: `Response body is not valid JSON: ${
            err instanceof Error ? err.message : String(err)
          }`,
        } as z.core.$ZodIssue,
      ],
      undefined,
    );
  }
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiResponseParseError(parsed.error.issues, raw);
  }
  return parsed.data;
}
